import React from "react"
import PropTypes from "prop-types"
import { Link } from "gatsby"

export const CoverageType = Object.freeze({
  Product: 0,
  Industry: 1,
})

import emailBullet from "../images/common/email-bullet.svg"
import phoneBullet from "../images/common/phone-bullet.svg"

const CoverageTemplate = ({
  icon,
  type,
  title,
  description,
  related,
  downloads,
  children,
}) => (
  <div className="container pt-20 pb-48 md:pt-24">
    <img className="h-40" src={icon} alt={title + " icon"} />
    <h1 className="h1 pt-8">{title}</h1>
    <p className="p1 pt-8 max-w-[51rem]">{description}</p>

    <div className="grid gap-20 lg:grid-cols-2">
      {/* Highlights column - LEFT */}
      <div className="mt-20">
        <h2 className="h2">
          {type === CoverageType.Product ? "Product" : "Industry"} highlights:
        </h2>
        <div className="coverage-highlights">{children}</div>
      </div>

      {/* Related and downloads column - RIGHT */}
      <div className="mt-20">
        {/*Related Box*/}
        <div className="p-10 md:p-20 rounded-2xl drop-shadow bg-[#f8f8f8]">
          <h3 className="links-title">
            {type === CoverageType.Product
              ? "Industries"
              : "Applicable Products"}
          </h3>
          <ul className="related-links mt-6">
            {related.map((link) => (
              <li key={link.name}>
                <Link className="underline" to={link.href}>
                  {link.name}
                </Link>
                {link.note && <span className="italic"> - {link.note}</span>}
              </li>
            ))}
          </ul>
        </div>

        {/*Downloads*/}
        <div className="p-10 md:p-20 mt-16">
          <h3 className="links-title">Downloads</h3>
          <ul className="download-links mt-6">
            {downloads.map((link) => (
              <li key={link.name}>
                <a href={link.href} target="_blank" rel="noopener noreferrer">
                  {link.name}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/*How to submit*/}
        <div className="p-10 md:p-20 rounded-2xl drop-shadow mt-16 bg-white">
          <h2 className="h2">How to submit:</h2>
          <p className="p1 mt-4">
            <a
              className="underline"
              href="https://app.sliceinsurance.com/e62c64fd-f0d5-4e2e-915b-20c4f04c1a53/landing?ref_agent_id=e62c64fd-f0d5-4e2e-915b-20c4f04c1a53"
              target="_blank"
              rel="nofollow noreferrer"
            >
              Login
            </a>{" "}
            or submit forms (
            <span className="italic">
              ACORD forms, supplementals, and loss runs
            </span>
            ) to{" "}
            <a
              className="underline"
              href="mailto:request@quote.sliceinsurance.com"
            >
              request@quote.sliceinsurance.com
            </a>
          </p>
          <h3 className="h3 mt-16">Need Help?</h3>
          <p className="p1 mt-4">
            Contact our customer service team or use the in-app chat to connect
            with us.
          </p>
          <ul className="p1">
            <li>
              <img
                src={emailBullet}
                className="inline mr-4 -translate-y-0.5"
                alt="email icon bullet point"
              />
              <a href="mailto:helpme@sliceinsurance.com">
                helpme@sliceinsurance.com
              </a>
            </li>
            <li>
              <img
                src={phoneBullet}
                className="inline mr-4 -translate-y-0.5"
                alt="phone icon bullet point"
              />
              <a href="tel:18556384465">1-855-638-4465</a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
)

const Linkable = Object.freeze({
  name: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  note: PropTypes.string,
})

CoverageTemplate.propTypes = {
  icon: PropTypes.object,
  type: PropTypes.oneOf([CoverageType.Product, CoverageType.Industry]),
  title: PropTypes.string,
  description: PropTypes.string,
  related: PropTypes.arrayOf(PropTypes.shape(Linkable)),
  downloads: PropTypes.arrayOf(PropTypes.shape(Linkable)),
  children: PropTypes.node,
}

export default CoverageTemplate
